import React, { useState } from "react";
import { PasswordInput } from "../../components/inputs/password-input";
import { Button } from "../../components/buttons/button";
import { Alert } from "../../components/alerts/alert";
import { setPopupAlertInLocalStorage } from "../../components/alerts/popup-alert";
import { validatePassword } from "../../utils/password-validator.ts";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem } from "../../components/base/form";
import { BadRequestModel } from "../../utils/bad-request-model.ts";

interface ChangePasswordProps {
  code: string;
}

const UserSchema = z
  .object({
    password: z.string({ required_error: "Please enter a password" }).refine((val) => validatePassword(val), {
      message: "Password needs to be at least eight characters and consist of letters, numbers and special characters",
    }),
    confirmPassword: z
      .string({ required_error: "Please confirm your password" })
      .refine((val) => validatePassword(val), {
        message:
          "Password needs to be at least eight characters and consist of letters, numbers and special characters",
      }),
  })
  .refine((val) => val.password === val.confirmPassword, {
    message: "The passwords do not match",
    path: ["confirmPassword"],
  });

export type FormFields = z.infer<typeof UserSchema>;

export const SetNewPasswordForm: React.FC<ChangePasswordProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const form = useForm<FormFields>({
    mode: "onTouched",
    resolver: zodResolver(UserSchema),
    reValidateMode: "onChange",
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    const requestBody = {
      Code: props.code,
      Password: form.getValues("password"),
      ConfirmPassword: form.getValues("confirmPassword"),
    };

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    };

    try {
      const response = await fetch("/users/new-change-password", requestOptions);
      if (response.ok) {
        setPopupAlertInLocalStorage({
          message: "Your password has been updated.  You've been signed in.",
          color: "green",
          style: "solid",
        });
        location.href = "/";
      } else {
        const responseBody = (await response.json()) as BadRequestModel;
        setSuccessful(false);
        setErrorMessage(responseBody.errors[""]?.[0] ?? "Unable to change password, please try again");
        setLoading(false);
      }
    } catch {
      setSuccessful(false);
      setErrorMessage("Unable to change password, please try again");
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4.5 md:gap-7.5" onSubmit={(event) => void onSubmit(event)}>
        {!successful && (
          <div className="mb-5">
            <Alert
              color="red"
              message={errorMessage}
              renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-triangle-exclamation" />}
              style="outlined"
              title="Error"
            />
          </div>
        )}
        <div className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name={"password"}
            render={() => (
              <FormItem>
                <FormControl>
                  <Controller
                    name={"password"}
                    control={form.control}
                    render={({ field: { onBlur, value, onChange } }) => (
                      <PasswordInput
                        state={form.formState.errors.password ? "error" : "default"}
                        id="user-password"
                        placeholder="Enter your password..."
                        label="New password"
                        labelSize="large"
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        message={form.formState.errors.password?.message}
                      />
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={"confirmPassword"}
            render={() => (
              <FormItem>
                <FormControl>
                  <Controller
                    name={"confirmPassword"}
                    control={form.control}
                    render={({ field: { onBlur, value, onChange } }) => (
                      <PasswordInput
                        state={form.formState.errors.confirmPassword ? "error" : "default"}
                        id="user-confirm-password"
                        placeholder="Re-enter your password..."
                        label="Confirm password"
                        labelSize="large"
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        message={form.formState.errors.confirmPassword?.message}
                      />
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <Button
          label="Reset password"
          colorVariant="primary"
          loading={loading}
          loadingLabel="Updating password..."
          variant="solid"
          disabled={loading || !form.getValues("password") || !form.getValues("confirmPassword")}
          type="submit"
        />
      </form>
    </Form>
  );
};
